import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { PlatformService } from '../core/platform/platform.service';
import { StorageService } from '../core/platform/storage.service';

type Language = 'pl' | 'en' | 'de';

const SUPPORTED_LANGUAGES: Language[] = ['pl', 'en', 'de'];
const STORAGE_KEY = 'app_language';
const DEFAULT_LANGUAGE: Language = 'pl';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  // Initialize with default, will be updated in constructor
  private currentLanguageSubject = new BehaviorSubject<Language>(DEFAULT_LANGUAGE);
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(
    private translate: TranslateService,
    private http: HttpClient,
    private platformService: PlatformService,
    private storageService: StorageService
  ) {
    // Detect and set actual initial language after platformId is available
    const actualLanguage = this.getInitialLanguage();
    this.currentLanguageSubject.next(actualLanguage);
  }

  /**
   * Initialize translations - must be called during APP_INITIALIZER
   * Loads all language files and configures TranslateService
   */
  initializeTranslations(): Observable<void> {
    return this.initializeLanguage();
  }

  /**
   * Get initial language from localStorage or browser or default
   * Priority: localStorage → browser language → Polish default
   */
  private getInitialLanguage(): Language {
    // Only check localStorage in browser (SSR-safe)
    if (this.platformService.isBrowser()) {
      const stored = this.storageService.getLocalItem(STORAGE_KEY) as Language | null;
      if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
        return stored;
      }
    }

    // Check browser language (on both browser and server, but navigator only exists on browser)
    if (this.platformService.isBrowser()) {
      const browserLang = this.platformService.getNavigatorLanguage().split('-')[0].toLowerCase() as Language;
      if (SUPPORTED_LANGUAGES.includes(browserLang)) {
        return browserLang;
      }
    }

    // Default to Polish
    return DEFAULT_LANGUAGE;
  }

  /**
   * Initialize translation service with supported languages
   * Loads all available language files and sets initial language
   */
  private initializeLanguage(): Observable<void> {
    const initialLang = this.currentLanguageSubject.value;

    // Configure supported languages
    this.translate.addLangs(SUPPORTED_LANGUAGES);
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);

    // Load all translation files in parallel
    const translationLoads = SUPPORTED_LANGUAGES.map(lang =>
      this.http.get(`./assets/i18n/${lang}.json`).pipe(
        map((translations: any) => {
          // Manually set translations for this language
          this.translate.setTranslation(lang, translations, true);
          return null;
        })
      )
    );

    // Wait for all translations to load, then set initial language
    return forkJoin(translationLoads).pipe(
      map(() => {
        // All translations loaded - now set the initial language
        this.translate.use(initialLang);
      })
    );
  }

  /**
   * Change current language
   */
  setLanguage(language: Language): void {
    if (!SUPPORTED_LANGUAGES.includes(language)) {
      console.warn(`Language ${language} not supported`);
      return;
    }

    this.translate.use(language);
    
    // Only save to localStorage in browser (SSR-safe)
    if (this.platformService.isBrowser()) {
      this.storageService.setLocalItem(STORAGE_KEY, language);
    }
    
    this.currentLanguageSubject.next(language);
  }

  /**
   * Get current language synchronously
   */
  getCurrentLanguage(): Language {
    return this.currentLanguageSubject.value;
  }

  /**
   * Get current language as observable
   */
  getCurrentLanguage$(): Observable<Language> {
    return this.currentLanguage$;
  }

  /**
   * Get all supported languages
   */
  getSupportedLanguages(): Language[] {
    return SUPPORTED_LANGUAGES;
  }

  /**
   * Get language label for UI
   */
  getLanguageLabel(lang: Language): string {
    const labels: Record<Language, string> = {
      'pl': '🇵🇱 Polski',
      'en': '🇬🇧 English',
      'de': '🇩🇪 Deutsch'
    };
    return labels[lang] || lang;
  }
}
